<template>
    <div>
        <v-container class="event-container">
            <v-row class="event-row">
                <!-- Left Panel: Event Details and Actions -->
                <v-col cols="12" md="5" class="pa-0 left-panel" style="border-right: 1px solid #eee;">
                    <v-container>
                        <event-details :event="event" :costs="costs" :notes="notes"
                            @show-notes="showNotesDialog = true">
                            <template v-slot:actions-menu>
                                <event-actions-menu
                                    :can-close-task="canCloseTask"
                                    :can-open-task="canOpenTask(dispatcherTask)"
                                    :can-edit-event="canEditEvent"
                                    :can-delete-event="canDeleteTask"
                                    :can-change-payments="canChangePayments"
                                    :is-event-for-me="isEventForMe"
                                    @start-dispatcher-task="startDispatcherTask"
                                    @close-all-tasks="closeAllTasks"
                                    @cancel-task="cancelTask"
                                    @assign-to-me="assignToMe"
                                    @assign-to-other="assignToOther"
                                    @release-from-me="releaseFromMe"
                                    @send-patrol="startPatrolTask"
                                    @found-users="foundUsers"
                                    @notify-users="notifyUsers"
                                    @set-payment="setPayment"
                                    @download="onDownload"
                                    @edit-event="onEditEvent"
                                    @delete-event="onDeleteEvent"
                                />
                            </template>
                        </event-details>

                        <event-action-buttons
                            :can-close-task="canCloseTask"
                            :can-open-task="canOpenTask(dispatcherTask)"
                            :can-edit-event="canEditEvent"
                            :can-delete-event="canDeleteTask"
                            :can-change-payments="canChangePayments"
                            :is-event-for-me="isEventForMe"
                            @start-dispatcher-task="startDispatcherTask"
                            @close-all-tasks="closeAllTasks"
                            @cancel-task="cancelTask"
                            @assign-to-me="assignToMe"
                            @assign-to-other="assignToOther"
                            @release-from-me="releaseFromMe"
                            @send-patrol="startPatrolTask"
                            @found-users="foundUsers"
                            @notify-users="notifyUsers"
                            @set-payment="setPayment"
                            @download="onDownload"
                            @edit-event="onEditEvent"
                            @delete-event="onDeleteEvent"
                        />
                    </v-container>
                </v-col>
                <v-col cols="12" md="7" class="pa-0 d-flex flex-column timeline-column">
                    <!-- Patrol Status - flex-shrink-0 aby nie kurczyły się -->
                    <div class="flex-shrink-0">
                        <v-col cols="12" class="d-flex" v-for="task of patrolTasks" :key="task.id">
                            <patrol-card-medium class="fill-height" :site="event.site" :task="task"
                                @update:task="addTask" :readOnly="UserTask.isReadOnly(task)"></patrol-card-medium>
                        </v-col>
                    </div>

                    <!-- Timeline - flex-grow-1 aby zajął pozostałą przestrzeń -->
                    <event-timeline class="mx-3 flex-grow-1" style="min-height: 0;" :tasks="allTasks" :event="event"
                        :dispatcher-task="dispatcherTask" :draft-messages="draftMessages"
                        @send-comment="handleSendComment" @task-updated="handleTaskUpdated" />
                </v-col>
            </v-row>
        </v-container>

        <!-- Notes Dialog -->
        <v-dialog v-model="showNotesDialog" max-width="800" scrollable>
            <v-card>
                <v-card-title class="d-flex align-center">
                    <v-icon class="mr-2">mdi-note-text</v-icon>
                    Notatki obiektu
                    <v-spacer></v-spacer>
                    <v-btn icon @click="showNotesDialog = false">
                        <v-icon>mdi-close</v-icon>
                    </v-btn>
                </v-card-title>
                <v-card-text style="max-height: 60vh;">
                    <site-notes :notes="notes" :site="event.site" :locked="false" />
                </v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn color="primary" text @click="showNotesDialog = false">
                        Zamknij
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
import EventService from "@/services/event.service"
import SiteService from "@/services/site.service"
import TaskService from "@/services/task.service"
import { UserTaskType, getTaskCost, isUserAssignedToTask, canEditCost, getTasksModifiedAt, getPrimaryEventTask } from "@/store/modules/task"
import UserTask from "@/store/modules/task"
import { sync } from 'vuex-pathify'
import { parseJSON } from "date-fns"
import EventDetails from "@/components/events/EventDetails.vue"
import EventTimeline from "@/components/events/EventTimeline.vue"
import EventActionsMenu from "@/components/events/EventActionsMenu.vue"
import EventActionButtons from "@/components/events/EventActionButtons.vue"
import SiteNotes from "@/components/SiteNotes.vue"
import PatrolCardMedium from "@/components/patrol/PatrolCardMedium.vue"
import { createEventActions } from "@/composables/useEventActions"

export default {
    name: "Event",

    metaInfo() {
        const siteName = this.event.site?.code_name || 'Unknown Site'
        const eventCode = this.event.code?.text || 'Event'
        return {
            title: `${siteName} - ${eventCode}`,
            meta: [
                {
                    name: "description",
                    content: `${siteName} - ${eventCode}`
                },
                {
                    property: "og:title",
                    content: `og:title = ${siteName} - ${eventCode}`
                },
                { property: "og:site_name", content: "og:site_name = Monpatrol" },
                {
                    property: "og:description",
                    content: `og:site_name = ${siteName} - ${eventCode}`
                },
                { property: "og:type", content: "og:type = profile" },
                {
                    property: "og:url",
                    content: "https://monpatrol.pl"
                },
                {
                    property: "og:image",
                    content: ""
                }
            ]
        }
    },
    beforeMount() {
        const eventId = this.$route.params.id
        EventService.fetch(eventId).then(response => {
            this.event = response.data
            // Ensure site is always an object to prevent undefined errors
            if (!this.event.site) {
                console.warn('Event API response missing site data for event:', eventId)
                this.event.site = {}
            }
            this.refreshNotes()
            this.addAll(response.data.tasks)
            this.lastNotifiedModifiedAt = this.modifiedAt
            this.hasLoadedInitialEvent = true
            this.$store.dispatch('dashboard/markEventAsSeen', eventId)
            this.timer = setInterval(this.refresh, 5000)
        })
    },
    beforeDestroy() {
        this.cancelAutoUpdate()
    },
    data: () => ({
        notes: [],
        showNotesDialog: false,
        timer: "",
        modifiedAt: null,
        lastNotifiedModifiedAt: null,
        hasLoadedInitialEvent: false,
        draftMessages: [], // Array to store draft messages

        event: {
            site: {},
            code: {
                text: ""
            },
            details: {},
            createdAt: ""
        },
        dispatcherTask: null,
        tasks: [],
        items: []
    }),
    filters: {},
    computed: {
        UserTask() {
            return UserTask
        },
        user: sync('user.profile'),
        patrolColumns() {
            return this.patrolTasks.length > 1 ? 6 : 6
        },
        allTasks() {
            //sum dispatcherTask and patrolTasks
            return [this.dispatcherTask, ...this.patrolTasks].filter(task => task != null)
        },
        eventReported() {
            return this.event.details?.reported_by?.full_name ?? null
        },
        eventCreated() {
            return this.event.details?.created_by?.full_name ?? null
        },
        notesCount() {
            return this.notes.length
        },
        costs() {
            let cost = getTaskCost(this.dispatcherTask, this.$store)
            return cost ? [cost] : []
        },
        title() {
            return this.dispatcherTask?.description
        },
        eventInfo() {
            return this.event.details?.info ?? null
        },
        dispatcherTaskMessage() {
            return this.dispatcherTask?.details?.status?.message ?? null
        },
        isEventForMe() {
            return isUserAssignedToTask(this.dispatcherTask, this.user.id)
        },
        dispatcherTaskComment() {
            return this.dispatcherTask?.details?.status?.comment ?? null
        },
        status() {
            return this.dispatcherTask?.details.status
        },
        statusText() {
            return null //this.dispatcherTask?.details.status?.message
        },
        canEditEvent() {
            return this.canCloseTask && !UserTask.isReadOnly(this.dispatcherTask)
        },
        canChangePayments() {
            return canEditCost(this.dispatcherTask)
        },
        canCloseTask() {
            return this.dispatcherTask && this.dispatcherTask.closed_at == null
        },
        canDeleteTask() {
            return this.user?.permissions?.event?.delete
        },
        created_by: function () {
            try {
                return this.dispatcherTask.created_by
            } catch {
                return null
            }
        },
        reported_by: function () {
            try {
                return this.dispatcherTask.details.reported_by
            } catch {
                return null
            }
        },
        canceledBy: function () {
            return this.dispatcherTask?.details.canceled ?? []
        },
        patrolTasks: function () {
            return this.tasks.filter(task => task.task_type == UserTaskType.PATROL)
        },
        notifiedUsers: function () {
            return this.dispatcherTask?.details?.notified ?? []
        },
        visibleEvent: function () {
            return this.event != null
        },
        visibleDetails: function () {
            return this.event.details.info != undefined
        },
        timeline() {
            return this.items.slice().reverse()
        },
        eventDate() {
            if (this.event.details.date) {
                return this.event.details.date
            } else {
                return this.event.created_at
            }
        }
    },
    components: {
        EventDetails,
        EventTimeline,
        EventActionsMenu,
        EventActionButtons,
        SiteNotes,
        PatrolCardMedium
    },
    created() {
        // Initialize action methods from composable
        this._eventActions = createEventActions(this)
    },
    methods: {
        // Delegate action methods to composable
        ...(() => {
            const actionNames = [
                'startDispatcherTask', 'closeAllTasks', 'cancelTask', 'startPatrolTask',
                'notifyUsers', 'foundUsers', 'setPayment', 'assignToMe', 'releaseFromMe',
                'assignToOther', 'onEditEvent', 'onDeleteEvent', 'onDownload'
            ]
            const delegateMethods = {}
            actionNames.forEach(name => {
                delegateMethods[name] = function(...args) {
                    return this._eventActions[name](...args)
                }
            })
            return delegateMethods
        })(),

        canOpenTask(task) {
            return task == null || (!this.canCloseTask && !UserTask.isReadOnly(task))
        },
        removeNotify(index) {
            const task = this.dispatcherTask
            const user = this.notifiedUsers[index]
            TaskService.deleteNotifiedUser(this.event.site.id, task.id, user).then(
                () => {
                    this.notifiedUsers.splice(index, 1)
                    this.refresh()
                }
            )
        },

        cancelAutoUpdate() {
            clearInterval(this.timer)
        },
        handleSendComment(payload) {
            // Save comment to temporary variable
            var commentToSend = payload.comment

            if (!commentToSend) {
                return
            }
            if (commentToSend.trim().length == 0) {
                return
            }

            // Add draft message to timeline immediately for better UX
            if (payload.draftMessage) {
                this.addDraftMessage(payload.draftMessage)
            }

            var lines = commentToSend.split('\n');
            var comment = commentToSend
            var more = null
            if (lines.length > 1) {
                comment = lines[0]
                lines.shift()
                more = lines.join('\n')
            }
            TaskService.addComment(this.event.site.id, payload.task.id, comment, more).then(res => {
                // Update draft message with real message data instead of removing it
                if (payload.draftMessage) {
                    this.updateDraftMessage(payload.draftMessage.id)
                }
                this.addTask(res.data)
                commentToSend = null
            }).catch(error => {
                // Remove draft message on error
                if (payload.draftMessage) {
                    this.removeDraftMessage(payload.draftMessage.id)
                }
                console.error('Error sending comment:', error)
            })
        },

        getPatrolName(patrol) {
            // Get patrol name from assignees or use default name
            if (patrol && patrol.assignees && patrol.assignees.length > 0) {
                const assignee = patrol.assignees[0]
                return assignee.user?.full_name || assignee.user?.username || `Patrol ${patrol.id}`
            }
            return `Patrol ${patrol?.id || 'nieznany'}`
        },

        addTask(task) {
            if (task == null) {
                return
            }
            this.$store.dispatch('dashboard/suppressLocalEventSignal', { eventId: this.event.id })
            this.addAll([task])
        },

        handleTaskUpdated(updatedTask) {
            // Handle updated task from EventTimeline
            this.addTask(updatedTask)
        },
        addAll(tasks) {
            if (!tasks) {
                return
            }
            //   const items = []
            for (const task of tasks) {
                this.addAll(task.subtasks)
                if (task.task_type == UserTaskType.DISPATCHER || (task.task_type == UserTaskType.SERVICE && this.dispatcherTask == null)) {
                    this.dispatcherTask = task
                }
                var index = this.tasks.findIndex(t => t.id == task.id) //ES
                // var index = this.tasks.map(task => task.id).indexOf(task.id);
                if (~index) {
                    // Use Vue.set to ensure reactivity for nested object changes
                    this.$set(this.tasks, index, task)
                } else {
                    index = this.tasks.length
                    this.tasks.push(task)
                }
                task.index = index
                if (this.modifiedAt == null || this.modifiedAt < task.modified_at) {
                    this.modifiedAt = task.modified_at
                }
            }
            if (this.dispatcherTask == null) {
                this.dispatcherTask = getPrimaryEventTask(this.tasks)
            }
            this.event.tasks = this.tasks
        },
        addDraftMessage(draftMessage) {
            if (!draftMessage) return
            this.draftMessages.push(draftMessage)
        },
        removeDraftMessage(draftMessageId) {
            const index = this.draftMessages.findIndex(msg => msg.id === draftMessageId)
            if (index > -1) {
                this.draftMessages.splice(index, 1)
            }
        },
        updateDraftMessage(draftMessageId) {
            const index = this.draftMessages.findIndex(msg => msg.id === draftMessageId)
            if (index > -1) {
                // Update draft status to 'received' to show success
                this.$set(this.draftMessages[index], 'status', 'received')

                // Remove the draft message after a short delay to allow smooth transition
                setTimeout(() => {
                    this.removeDraftMessage(draftMessageId)
                }, 500) // 500ms delay for smooth UI transition
            }
        },

        refreshNotes() {
            const args = {
                conditions: [`date=${parseJSON(this.event.created_at).toISOString()}`]
            }
            SiteService.getNotes(this.event.site.id, args).then(response => {
                this.notes = response.data
            })
        },
        refresh() {
            if (this.modifiedAt == null) {
                this.modifiedAt = new Date(0).toISOString()
            }
            this.refreshNotes()
            this.refreshEvent()
        },
        refreshEvent() {
            const wasOpen = this.canCloseTask
            TaskService.fetch(
                this.event.id,
                "modified_after=" + this.modifiedAt
            ).then(response => {
                const updatedTasks = response.data || []
                if (updatedTasks.length > 0) {
                    const latestIncomingModifiedAt = getTasksModifiedAt(updatedTasks)

                    this.addAll(updatedTasks)
                    this.$store.dispatch('dashboard/markEventAsSeen', this.event.id)

                    const isClosedTransition = wasOpen && !this.canCloseTask
                    const shouldNotify =
                        this.hasLoadedInitialEvent &&
                        latestIncomingModifiedAt &&
                        latestIncomingModifiedAt !== this.lastNotifiedModifiedAt

                    if (shouldNotify) {
                        this.$dialog.notify.info(
                            isClosedTransition ? 'Zdarzenie zostalo zamkniete' : 'Zdarzenie zostalo zaktualizowane'
                        )
                        this.lastNotifiedModifiedAt = latestIncomingModifiedAt
                    }
                }
            })
        }
    }
}
</script>

<style scoped>
.event-container {
    height: 100vh;
    max-height: 100vh;
    overflow: hidden;
    padding: 12px !important;
}

.event-row {
    height: 100%;
    margin: 0 !important;
}

@media (max-width: 959px) {
    .event-container {
        height: auto;
        max-height: none;
        overflow: visible;
    }

    .event-row {
        height: auto;
    }
}

.note {
    white-space: pre-wrap;
    word-break: keep-all;
    overflow-wrap: break-word;
    font-family: inherit;
}

.left-panel {
    height: 100%;
    overflow-y: auto;
}

@media (max-width: 959px) {
    .left-panel {
        height: auto;
        overflow-y: visible;
        border-right: none !important;
        border-bottom: 1px solid #eee;
    }

    .timeline-column {
        height: auto;
        min-height: 60vh;
    }
}

.timeline-column {
    height: calc(100% - 3em);
    overflow: hidden;
}
</style>
